import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  Ban,
  ChevronRight,
  History,
  Loader2,
  Play,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SoftDeletePromptDialog } from "../components/platform/SoftDeletePromptDialog";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PlatformApiError } from "../lib/platform-api";
import type {
  BuiltPrompt,
  ExecuteResult,
  PromptVersion,
  SlugSummary,
  VariableSpec,
} from "../types/platform-prompts";
import { CompositionGate } from "@/components/specialists/CompositionGate";
import { specialistIdFromSlug } from "@/lib/composition-gate";

/** STATUS filter values. ENABLED is the default — hides DISABLED + DELETED
 *  (recycle-bin parity with the Organizations Recycle Bin pattern). */
type StatusFilter = "ENABLED" | "DISABLED" | "DELETED" | "ALL";
/** TYPE filter — code (locked / boot-seeded) vs operator (UI-authored). */
type TypeFilter = "ALL" | "code" | "operator";

function statusOf(s: SlugSummary): "enabled" | "disabled" | "deleted" {
  if (s.deleted) return "deleted";
  if (s.disabled) return "disabled";
  return "enabled";
}

/**
 * LexPortal "Platform Prompts" page — the versioned, operator-editable prompt
 * registry (ai-docs/lexserver-prompts-implementation-plan.md §11). Two surfaces:
 *   - Build: create/edit (new version), version history + set-active/roll-back,
 *     disable. The "active" version (the `production` label) is what every
 *     request resolves; saving an edit does NOT change it.
 *   - Execute: build vars → run via /api/llm/execute (PLATFORM default creds).
 * PLATFORM_ADMIN only (server-enforced).
 */
function errMsg(e: unknown, fallback: string): string {
  return e instanceof PlatformApiError
    ? `${e.code}: ${e.message}`
    : e instanceof Error
      ? e.message
      : fallback;
}

const textareaCls =
  "w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm font-mono " +
  "focus:outline-none focus:ring-2 focus:ring-ring";

export function PlatformPrompts() {
  const api = usePlatformApi();

  const [slugs, setSlugs] = useState<SlugSummary[]>([]);
  // The open prompt is URL-driven (`?slug=`): clicking a row navigates to
  // the editor view; the Back button clears it to return to the list.
  // Single source of truth → deep-links from the "LLM Calls" blade land
  // on the editor, and browser back/forward work.
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get("slug");
  const setSelected = useCallback(
    (slug: string | null) => {
      setSearchParams(slug ? { slug } : {}, { replace: false });
    },
    [setSearchParams],
  );
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Editor state (the currently-loaded/edited version).
  const [body, setBody] = useState("");
  const [varsJson, setVarsJson] = useState("[]");
  const [description, setDescription] = useState("");
  const [defaultProfile, setDefaultProfile] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  // Version pending a rollback confirmation (set-active on an OLDER version).
  const [confirmRollback, setConfirmRollback] = useState<number | null>(null);
  /** The version whose composition gate is open, if any (specialist personas only). */
  const [gateCandidate, setGateCandidate] = useState<number | null>(null);

  // Recycle-bin destructive-confirm dialog (mirrors the org soft-delete
  // pattern — typed "I UNDERSTAND" + 3-500 char audited reason).
  const [softDeleteOpen, setSoftDeleteOpen] = useState(false);
  const [softDeleteError, setSoftDeleteError] = useState<string | null>(null);

  // STATUS + TYPE filters are client-side over the already-loaded slugs (the
  // registry is small). STATUS defaults to ENABLED so the recycle bin is
  // hidden on first load; TYPE defaults to ALL.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ENABLED");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const filtersActive =
    statusFilter !== "ENABLED" || typeFilter !== "ALL";

  const loadSlugs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPlatformPrompts();
      setSlugs(res.data.prompts);
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to load prompts"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadVersions = useCallback(
    async (slug: string) => {
      try {
        const res = await api.getPromptVersions(slug);
        setVersions(res.data.versions);
        const newest = res.data.versions[0];
        if (newest) {
          setBody(newest.body);
          setVarsJson(JSON.stringify(newest.variables, null, 2));
          setDescription(newest.description ?? "");
          setDefaultProfile(newest.defaultProfile ?? "");
        }
        setError(null);
      } catch (e) {
        setError(errMsg(e, "Failed to load versions"));
      }
    },
    [api],
  );

  useEffect(() => {
    void loadSlugs();
  }, [loadSlugs]);

  useEffect(() => {
    setConfirmRollback(null);
    if (selected) void loadVersions(selected);
  }, [selected, loadVersions]);

  const current = slugs.find((s) => s.slug === selected) ?? null;

  // Filtering is display-only — the underlying `slugs` state stays unfiltered
  // so deep-links into a deleted prompt (?slug=...) still find `current` and
  // render the detail-view banner.
  const visibleSlugs = useMemo(() => {
    return slugs.filter((s) => {
      if (statusFilter !== "ALL") {
        if (statusOf(s) !== statusFilter.toLowerCase()) return false;
      }
      if (typeFilter !== "ALL" && s.source !== typeFilter) return false;
      return true;
    });
  }, [slugs, statusFilter, typeFilter]);

  function parseVars(): VariableSpec[] {
    const parsed = JSON.parse(varsJson) as VariableSpec[];
    if (!Array.isArray(parsed)) throw new Error("variables must be a JSON array");
    return parsed;
  }

  async function handleSaveNewVersion() {
    if (!selected) return;
    setBusy("save");
    try {
      const variables = parseVars();
      await api.updatePlatformPrompt(selected, {
        body,
        variables,
        description: description || undefined,
        defaultProfile: defaultProfile || undefined,
      });
      await loadVersions(selected);
      await loadSlugs();
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to save new version"));
    } finally {
      setBusy(null);
    }
  }

  // Set `version` as the ACTIVE version every request resolves (the
  // `production` label). Promoting a NEWER version = go live with an edit;
  // promoting an OLDER version = roll back. Same single label-move either way.
  // ⚖ A specialist persona's label move is GATED (§4-ter), so clicking promote
  // on one selects a CANDIDATE rather than promoting it: the gate loads that
  // triple's recorded verdict, and only an affirmative pass exposes the button
  // that actually moves the label. Every other prompt promotes exactly as
  // before — a gate that widened its own scope would break unrelated prompt
  // operations with a specialists error.
  async function requestSetActive(version: number) {
    if (!selected) return;
    if (specialistIdFromSlug(selected) !== null) {
      setGateCandidate(version);
      setConfirmRollback(null);
      return;
    }
    await handleSetActive(version);
  }

  async function handleSetActive(version: number) {
    if (!selected) return;
    setBusy(`promote-${version}`);
    try {
      await api.promotePromptLabel(selected, "production", version);
      await loadSlugs();
      setConfirmRollback(null);
      setGateCandidate(null);
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to set active version"));
    } finally {
      setBusy(null);
    }
  }

  async function handleToggleDisabled() {
    if (!current) return;
    setBusy("toggle");
    try {
      if (current.disabled) await api.enablePlatformPrompt(current.slug);
      else await api.disablePlatformPrompt(current.slug);
      await loadSlugs();
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to toggle disabled"));
    } finally {
      setBusy(null);
    }
  }

  async function handleSoftDelete(reason: string) {
    if (!current) return;
    setBusy("soft-delete");
    setSoftDeleteError(null);
    try {
      await api.softDeletePlatformPrompt(current.slug, reason);
      await loadSlugs();
      setSoftDeleteOpen(false);
      setSelected(null); // back to list — show STATUS=ENABLED hides the deleted row
      setError(null);
    } catch (e) {
      // Surface the failure inside the dialog so the operator can retry without
      // losing the typed reason.
      setSoftDeleteError(errMsg(e, "Failed to soft-delete prompt"));
    } finally {
      setBusy(null);
    }
  }

  // Restore handler used by BOTH the list-view ACTIONS column button and the
  // detail-view banner. Mirrors handleToggleDisabled's error path — sets the
  // top-of-page `prompts-error` banner on failure.
  async function handleRestore(slug: string) {
    setBusy(`restore-${slug}`);
    try {
      await api.restorePlatformPrompt(slug);
      await loadSlugs();
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to restore prompt"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="platform-prompts-page">
      {error && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="prompts-error"
        >
          {error}
        </div>
      )}

      {!selected ? (
        /* ─────────────── LIST VIEW ─────────────── */
        <>
          {/* Title row: heading + description on the left, +New prompt button
              right-aligned on the same row. Filters live in a separate row
              below the title so the description never bumps them off-screen
              on narrower viewports. */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold">Prompts</h1>
              <p className="text-sm text-muted-foreground">
                Versioned, platform-wide prompt registry. Every request resolves
                the <strong>active</strong> version of a prompt. Click a prompt to
                view + edit its versions.
              </p>
            </div>
            <Button onClick={() => setShowCreate(true)} data-testid="new-prompt-btn">
              <Plus className="mr-1 h-4 w-4" /> New prompt
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground flex items-center gap-1">
              Status
              <select
                data-testid="prompts-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="rounded-md border border-border bg-panel-2 px-2 py-1 text-xs text-foreground"
              >
                <option value="ENABLED">Enabled</option>
                <option value="DISABLED">Disabled</option>
                <option value="DELETED">Deleted</option>
                <option value="ALL">All</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground flex items-center gap-1">
              Type
              <select
                data-testid="prompts-type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                className="rounded-md border border-border bg-panel-2 px-2 py-1 text-xs text-foreground"
              >
                <option value="ALL">All</option>
                <option value="code">Code</option>
                <option value="operator">Operator</option>
              </select>
            </label>
          </div>

          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28">Source</TableHead>
                  <TableHead
                    className="w-28 text-right"
                    title="active version / latest version"
                  >
                    Active / Latest
                  </TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  visibleSlugs.map((s) => (
                    <TableRow
                      key={s.slug}
                      className="cursor-pointer hover:bg-accent/40"
                      onClick={() => setSelected(s.slug)}
                      data-testid={`prompt-row-${s.slug}`}
                    >
                      <TableCell>
                        <span className="font-mono text-sm">{s.slug}</span>
                      </TableCell>
                      <TableCell>
                        {s.deleted ? (
                          <Badge
                            variant="destructive"
                            data-testid="prompt-status-deleted"
                          >
                            deleted
                          </Badge>
                        ) : s.disabled ? (
                          <Badge variant="destructive">disabled</Badge>
                        ) : s.productionVersion != null ? (
                          <Badge variant="default">active</Badge>
                        ) : (
                          <Badge variant="outline">no active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={s.source === "code" ? "secondary" : "outline"}>
                          {s.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {s.productionVersion ?? "–"} / {s.latestVersion}
                      </TableCell>
                      <TableCell className="max-w-md truncate text-sm text-muted-foreground">
                        {s.description || "—"}
                      </TableCell>
                      <TableCell>
                        {s.deleted && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy === `restore-${s.slug}`}
                            data-testid={`restore-prompt-${s.slug}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRestore(s.slug);
                            }}
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            Restore
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        <ChevronRight className="h-4 w-4" />
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && visibleSlugs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-muted-foreground">
                      {slugs.length === 0
                        ? "No prompts yet."
                        : filtersActive
                          ? "No prompts match the current filters."
                          : "No prompts to display."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      ) : !current ? (
        /* selected via URL but not loaded yet / not found */
        loading ? (
          <div className="flex items-center gap-2 p-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading prompt…
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3 p-12">
            <p className="text-sm text-muted-foreground">
              Prompt <span className="font-mono">{selected}</span> not found.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelected(null)}
              data-testid="prompt-back-btn"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to prompts
            </Button>
          </div>
        )
      ) : (
        /* ─────────────── DETAIL / EDITOR VIEW ─────────────── */
        <div className="flex flex-col gap-4" data-testid="prompt-detail">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelected(null)}
              data-testid="prompt-back-btn"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
              <h2 className="font-mono text-lg">{current.slug}</h2>
              <Badge variant="default" data-testid="active-version-indicator">
                active: v{current.productionVersion ?? "–"}
              </Badge>
              <span className="text-xs text-muted-foreground">
                latest: v{current.latestVersion}
              </span>
              <div className="ml-auto flex gap-2">
                {/* Enable/Disable is meaningless once the prompt is soft-deleted —
                    softDelete clears `_disabled` and delete supersedes disable.
                    The deleted-banner below exposes Restore as the only action. */}
                {!current.deleted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleDisabled}
                    disabled={busy === "toggle"}
                    data-testid="toggle-disabled-btn"
                  >
                    <Ban className="mr-1 h-4 w-4" />
                    {current.disabled ? "Enable" : "Disable"}
                  </Button>
                )}
                {!current.deleted && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setSoftDeleteError(null);
                      setSoftDeleteOpen(true);
                    }}
                    disabled={busy === "soft-delete"}
                    data-testid="soft-delete-btn"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Soft-delete
                  </Button>
                )}
              </div>
            </div>

            {/* Deep-link landing on a soft-deleted prompt — banner + inline Restore. */}
            {current.deleted && (
              <div
                className="rounded-md border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical flex items-center justify-between gap-3"
                data-testid="prompt-deleted-banner"
              >
                <span>
                  This prompt is <strong>soft-deleted</strong>. Restore it to
                  resume production resolution.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === `restore-${current.slug}`}
                  data-testid={`restore-prompt-${current.slug}`}
                  onClick={() => void handleRestore(current.slug)}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Restore
                </Button>
              </div>
            )}

            {/* Body + metadata editor */}
            <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
              <label className="text-xs font-medium text-muted-foreground">Body</label>
              <textarea
                className={textareaCls}
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="prompt-body"
              />
              <label className="text-xs font-medium text-muted-foreground">
                Variables (JSON array of {"{ name, type, required }"})
              </label>
              <textarea
                className={textareaCls}
                rows={4}
                value={varsJson}
                onChange={(e) => setVarsJson(e.target.value)}
                data-testid="prompt-variables"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <Input
                  placeholder="defaultProfile (e.g. LOW)"
                  value={defaultProfile}
                  onChange={(e) => setDefaultProfile(e.target.value)}
                />
              </div>
              <Button
                className="self-start"
                onClick={handleSaveNewVersion}
                disabled={busy === "save"}
                data-testid="save-version-btn"
              >
                {busy === "save" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Save as new version
              </Button>
            </div>

            {/* Version history */}
            <div className="rounded-lg border border-border p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                <History className="h-4 w-4" /> Version history
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                The <strong>active</strong> version is the one every request uses.
                Use <strong>Set active</strong> to make a newer version live, or{" "}
                <strong>Roll back</strong> to make an earlier version active again
                if a newer one misbehaves.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Ver</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-40 text-right">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((v) => {
                    const activeVersion = current.productionVersion;
                    const isActive = activeVersion === v.version;
                    const isOlder =
                      activeVersion != null && v.version < activeVersion;
                    return (
                      <TableRow key={v.version} data-testid={`version-${v.version}`}>
                        <TableCell className="tabular-nums">{v.version}</TableCell>
                        <TableCell>
                          <Badge variant={v.source === "code" ? "secondary" : "outline"}>
                            {v.source}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(v.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {isActive ? (
                            <Badge data-testid={`active-badge-${v.version}`}>active</Badge>
                          ) : isOlder ? (
                            confirmRollback === v.version ? (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => void requestSetActive(v.version)}
                                disabled={busy === `promote-${v.version}`}
                                data-testid={`promote-${v.version}`}
                                title={`Make v${v.version} the active version (roll back from v${activeVersion})`}
                              >
                                Confirm roll back → v{v.version}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmRollback(v.version)}
                                data-testid={`rollback-${v.version}`}
                                title={`Roll back: make the older v${v.version} active again`}
                              >
                                ↩ Roll back
                              </Button>
                            )
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void requestSetActive(v.version)}
                              disabled={busy === `promote-${v.version}`}
                              data-testid={`promote-${v.version}`}
                              title={`Make v${v.version} the active version`}
                            >
                              Set active
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {gateCandidate !== null && (
              <CompositionGate
                slug={current.slug}
                personaVersion={gateCandidate}
                // Half the verdict's lookup key, and the page already has it —
                // no extra fetch. `null` when the base row is missing or has no
                // production version, which the gate renders as could-not-read.
                baseVersion={
                  slugs.find((x) => x.slug === "harn:specialist-base")?.productionVersion ?? null
                }
                promoting={busy === `promote-${gateCandidate}`}
                onCancel={() => setGateCandidate(null)}
                onPromote={() => void handleSetActive(gateCandidate)}
              />
            )}

            <PromptExecutor slug={current.slug} />
        </div>
      )}

      {showCreate && (
        <CreatePromptDialog
          onClose={() => setShowCreate(false)}
          onCreated={async (slug) => {
            setShowCreate(false);
            await loadSlugs();
            setSelected(slug);
          }}
        />
      )}

      {current && (
        <SoftDeletePromptDialog
          open={softDeleteOpen}
          onOpenChange={(open) => {
            setSoftDeleteOpen(open);
            if (!open) setSoftDeleteError(null);
          }}
          slug={current.slug}
          source={current.source}
          wasDisabled={current.disabled}
          busy={busy === "soft-delete"}
          error={softDeleteError}
          onConfirm={(reason) => handleSoftDelete(reason)}
        />
      )}
    </div>
  );
}

/** Build vars → run via /api/llm/execute. Platform default credentials.
 *
 *  Profile dropdown is catalog-driven: options come from the platform
 *  `llmProfiles.profiles` keyset (canonical source per the 2026-05-26
 *  schema loosening — see lx-llm-resolver/src/schema.ts). The bare BASE
 *  names get fanned out into the resolver's 5 thinking overlays
 *  (`<BASE>:<level>` with level ∈ minimal|low|medium|high|xhigh) so the
 *  operator can pick e.g. `MAX:xhigh` from a single dropdown without
 *  remembering the suffix syntax. Composite entries in the catalog
 *  appear verbatim. As the operator changes selection, GET /resolve
 *  surfaces the actual decision (provider/model + thinking overlay)
 *  inline so the notation isn't a black box. */
const THINKING_OVERLAYS = [
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
] as const;

interface ResolvedDecision {
  provider: string;
  model: string;
  thinkingDefault: string | null;
}

function PromptExecutor({ slug }: { slug: string }) {
  const api = usePlatformApi();
  const [varsJson, setVarsJson] = useState("{}");
  const [userInput, setUserInput] = useState("");
  const [profile, setProfile] = useState<string | null>(null);
  const [profileOptions, setProfileOptions] = useState<string[]>([]);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolvedDecision | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [built, setBuilt] = useState<BuiltPrompt | null>(null);
  const [result, setResult] = useState<ExecuteResult | null>(null);
  const [running, setRunning] = useState(false);
  const [execError, setExecError] = useState<string | null>(null);

  // Load the platform catalog → derive the dropdown options + default
  // selection. The keyset is the canonical source per
  // lx-llm-resolver/src/schema.ts:55-65 — we don't hardcode MAX/MED/LOW.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPlatformLlmProfiles();
        if (cancelled) return;
        const catalog = res.data;
        if (!catalog || Object.keys(catalog.profiles).length === 0) {
          setProfileLoadError(
            "Platform LLM profiles catalog is unset — save one via Platform Settings → LLM Profiles.",
          );
          return;
        }
        // Bare BASE keys × thinking overlays (the resolver's
        // <BASE>:<thinking> notation), plus any composite entries the
        // operator authored directly. Deduplicated; bare BASE first.
        const bases = Object.keys(catalog.profiles).filter((n) => !n.includes(":"));
        const composites = Object.keys(catalog.profiles).filter((n) => n.includes(":"));
        const expanded: string[] = [];
        for (const base of bases) {
          expanded.push(base);
          for (const level of THINKING_OVERLAYS) expanded.push(`${base}:${level}`);
        }
        for (const name of composites) {
          if (!expanded.includes(name)) expanded.push(name);
        }
        setProfileOptions(expanded);
        // Default selection: prefer the catalog's defaultChatProfile if it
        // appears in the option list; else fall back to first BASE.
        const defaultPick =
          catalog.defaultChatProfile && expanded.includes(catalog.defaultChatProfile)
            ? catalog.defaultChatProfile
            : (bases[0] ?? expanded[0]);
        if (defaultPick) setProfile(defaultPick);
      } catch (e) {
        if (!cancelled) setProfileLoadError(errMsg(e, "Failed to load profile catalog"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Re-fetch the resolved decision whenever profile changes. Pure server-side
  // resolver lookup, no LLM call — cheap, gives immediate feedback that the
  // notation maps to a real (provider, model) pair.
  useEffect(() => {
    if (!profile) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolving(true);
      setResolveError(null);
      try {
        const r = await api.resolvePlatformLlmProfile(profile);
        if (cancelled) return;
        setResolved({
          provider: r.data.provider,
          model: r.data.model,
          thinkingDefault: r.data.thinkingDefault,
        });
      } catch (e) {
        if (!cancelled) {
          setResolved(null);
          setResolveError(errMsg(e, "Resolve failed"));
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, profile]);

  async function handleRun() {
    if (!profile) return;
    setRunning(true);
    setExecError(null);
    setResult(null);
    try {
      const vars = JSON.parse(varsJson) as Record<string, unknown>;
      const b = await api.buildPrompt(slug, vars);
      setBuilt(b.data);
      const messages = userInput
        ? [{ role: "user", content: userInput }]
        : [{ role: "user", content: "(no input)" }];
      const r = await api.executeLlm({
        system: b.data.system,
        profile,
        messages,
        // Tag the run so it surfaces in the Observability "LLM Calls"
        // blade Origin column (source=platform-executor) + carries the
        // prompt slug+version it executed.
        meta: {
          promptSlug: slug,
          promptVersion: b.data.meta.version,
          origin: { source: "platform-executor" },
        },
      });
      setResult(r.data);
    } catch (e) {
      setExecError(errMsg(e, "Execution failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-lg border border-border p-4" data-testid="prompt-executor">
      {/* Header row: label + creds-note on the left; profile dropdown,
          resolved-model preview, and Run button right-aligned. Per the
          user's request, this is the executor's first row. */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Play className="h-4 w-4" /> Execute
          <Badge variant="outline" data-testid="default-creds-note">
            using default provider credentials
          </Badge>
        </div>
        {/* Right-aligned cluster, internally a 2-row column so the resolved
            model preview lines up below the Profile label/dropdown row. */}
        <div className="flex flex-col items-start gap-1 ml-auto">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground flex items-center gap-1">
              Profile
              <select
                value={profile ?? ""}
                onChange={(e) => setProfile(e.target.value)}
                disabled={profileOptions.length === 0}
                data-testid="exec-profile"
                className="rounded-md border border-border bg-panel-2 px-2 py-1 text-sm text-foreground disabled:opacity-60"
              >
                {profileOptions.length === 0 && (
                  <option value="">(no profiles)</option>
                )}
                {profileOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <Button
              onClick={handleRun}
              disabled={running || !profile || !!profileLoadError}
              data-testid="exec-run-btn"
            >
              {running ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-1 h-4 w-4" />
              )}
              Run
            </Button>
          </div>
          <span
            className="text-xs text-muted-foreground font-mono"
            data-testid="exec-resolved-model"
          >
            {resolving
              ? "resolving…"
              : resolveError
                ? `↳ ${resolveError}`
                : resolved
                  ? `↳ ${resolved.provider}/${resolved.model}${resolved.thinkingDefault ? ` · think=${resolved.thinkingDefault}` : ""}`
                  : ""}
          </span>
        </div>
      </div>

      {profileLoadError && (
        <div
          className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="exec-profile-load-error"
        >
          {profileLoadError}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">
          Variables (JSON object)
        </label>
        <textarea
          className={textareaCls}
          rows={3}
          value={varsJson}
          onChange={(e) => setVarsJson(e.target.value)}
          data-testid="exec-vars"
        />
        <Input
          placeholder="test user message"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          data-testid="exec-user-input"
        />
      </div>

      {execError && (
        <div
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="exec-error"
        >
          {execError}
        </div>
      )}
      {built && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Built system prompt (v{built.meta.version})
          </summary>
          <pre className="mt-1 whitespace-pre-wrap rounded bg-panel-2 p-2">
            {built.system}
          </pre>
        </details>
      )}
      {result && (
        <div className="mt-3" data-testid="exec-result">
          <pre className="whitespace-pre-wrap rounded bg-panel-2 p-3 text-sm">
            {result.text}
          </pre>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {result.provider}/{result.model} · {result.usage.totalTokens} tokens
              · ${result.usage.cost.total.toFixed(4)} · {result.durationMs}ms
            </p>
            {/* Cross-link to the Observability blade — this run emitted a
                lx_llm_caller.call_complete record, which is the blade's
                default "All LLM calls" view, so the just-run call appears
                newest-first. (The executor response doesn't carry the
                trace_id, so we link to the list rather than a single
                trace.) */}
            <Link
              to="/observability/llm-calls"
              data-testid="exec-view-in-llm-calls"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Activity className="h-3 w-3" /> View in LLM Calls
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function CreatePromptDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (slug: string) => void | Promise<void>;
}) {
  const api = usePlatformApi();
  const [slug, setSlug] = useState("");
  const [body, setBody] = useState("");
  const [varsJson, setVarsJson] = useState("[]");
  const [defaultProfile, setDefaultProfile] = useState("LOW");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleCreate() {
    setSubmitting(true);
    setErr(null);
    try {
      const variables = JSON.parse(varsJson) as VariableSpec[];
      await api.createPlatformPrompt({
        slug,
        body,
        variables,
        defaultProfile: defaultProfile || undefined,
      });
      await onCreated(slug);
    } catch (e) {
      setErr(errMsg(e, "Failed to create prompt"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New platform prompt</DialogTitle>
          <DialogDescription>
            Creates v1 (automatically set as the active version). Slug: lower-snake /
            kebab; <code>_</code>-prefix marks a reusable {"{{partial}}"} fragment.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Input
            placeholder="slug (e.g. case-intake-summary)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="create-slug"
          />
          <textarea
            className={textareaCls}
            rows={8}
            placeholder="prompt body — {{token}} + {{partial:slug}}"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="create-body"
          />
          <textarea
            className={textareaCls}
            rows={3}
            placeholder='variables, e.g. [{"name":"x","type":"string","required":true}]'
            value={varsJson}
            onChange={(e) => setVarsJson(e.target.value)}
            data-testid="create-variables"
          />
          <Input
            placeholder="defaultProfile (e.g. LOW)"
            value={defaultProfile}
            onChange={(e) => setDefaultProfile(e.target.value)}
          />
          {err && <div className="text-sm text-destructive">{err}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={submitting} data-testid="create-submit">
              {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
